import { NavLink } from "react-router-dom";
import { useEffect } from "react";
import * as amplitude from "@amplitude/analytics-browser";
import { APP_STORE_URL, GOOGLE_PLAY_URL, gameCategories, parentGuides } from "./content";
import usePageMetadata from "./usePageMetadata";

export default function MarketingHome() {
    usePageMetadata({
        title: "Fun Math Games for Kids",
        description: "Explore MathMinds math and brain games for children, with activities for counting, addition, multiplication, fractions, memory, and more.",
    });

    useEffect(() => {
        amplitude.track("app_start_home");
    }, []);

    return (
        <>
            <section className="overflow-hidden bg-[linear-gradient(180deg,#FFF8DF_0%,#FCF9EE_100%)]">
                <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-5 py-16 sm:px-8 md:grid-cols-2 md:py-24">
                    <div>
                        <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">Learn through play</p>
                        <h1 className="mt-4 max-w-2xl font-chango text-4xl leading-tight text-[#327560] sm:text-5xl lg:text-6xl">
                            Fun math games that grow with your child
                        </h1>
                        <p className="mt-6 max-w-xl text-lg leading-8 text-[#56635E]">
                            Practice number skills, memory, logic, and problem solving with colorful games made for children ages 4–15+.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <NavLink to="/games/memory" className="rounded-full bg-[#F9B818] px-7 py-3.5 font-bold shadow-sm hover:bg-[#FFCA47]">
                                Start playing
                            </NavLink>
                            <NavLink to="/math-games/" className="rounded-full border-2 border-[#327560] px-7 py-3 font-bold text-[#327560] hover:bg-[#E8F3EC]">
                                Parent guides
                            </NavLink>
                        </div>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" aria-label="Download MathMinds on the App Store">
                                <img src="/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-10 w-auto object-contain" />
                            </a>
                            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" aria-label="Get MathMinds on Google Play">
                                <span className="flex h-10 items-center overflow-hidden">
                                    <img src="/badges/get-it-on-google-play.png" alt="Get it on Google Play" className="h-[59px] w-auto max-w-none object-contain" />
                                </span>
                            </a>
                        </div>
                    </div>
                    <div className="relative mx-auto w-full max-w-lg rounded-[3rem] bg-[#DDF5ED] p-8 shadow-[0_20px_60px_rgba(50,117,96,0.16)]">
                        <img src="/backGrounds/welcome_message.png" alt="MathMinds characters welcoming children to learn" className="mx-auto w-full" />
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-[1200px] px-5 py-16 sm:px-8">
                <div className="max-w-2xl">
                    <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">Choose a skill</p>
                    <h2 className="mt-3 font-chango text-3xl text-[#327560] sm:text-4xl">Math and brain games</h2>
                    <p className="mt-4 leading-7 text-[#56635E]">Go directly to a game category and choose an activity at the right level.</p>
                </div>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {gameCategories.map((category) => (
                        <NavLink key={category.name} to={category.path} className={`${category.color} rounded-3xl p-6 text-xl font-bold transition-transform hover:-translate-y-1`}>
                            {category.name}<span aria-hidden="true" className="float-right">→</span>
                        </NavLink>
                    ))}
                </div>
            </section>

            <section className="bg-white/70">
                <div className="mx-auto max-w-[1200px] px-5 py-16 sm:px-8">
                    <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">For parents</p>
                    <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
                        <h2 className="font-chango text-3xl text-[#327560] sm:text-4xl">Find games for your child</h2>
                        <NavLink to="/math-games/" className="font-bold text-[#327560] hover:underline">View all parent guides →</NavLink>
                    </div>
                    <div className="mt-8 grid gap-5 md:grid-cols-3">
                        {parentGuides.slice(0, 3).map((guide) => (
                            <NavLink key={guide.path} to={guide.path} className="rounded-3xl border border-[#E9E2CD] bg-[#FCF9EE] p-6 shadow-sm transition hover:border-[#86B7A5]">
                                <h3 className="text-xl font-bold text-[#327560]">{guide.label}</h3>
                                <p className="mt-3 leading-7 text-[#56635E]">{guide.description}</p>
                            </NavLink>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-[1200px] px-5 py-16 sm:px-8">
                <div className="grid gap-6 md:grid-cols-3">
                    <article className="rounded-3xl bg-[#FFF0C9] p-7">
                        <h2 className="font-chango text-2xl leading-tight text-[#327560]">Math games for every learning stage</h2>
                        <p className="mt-4 leading-7 text-[#56635E]">Help children build math skills at their own pace with activities designed for different ages and ability levels. Younger learners can practice counting and number recognition, while older children can work on multiplication, division, fractions, and more challenging problems.</p>
                    </article>
                    <article className="rounded-3xl bg-[#DDF5ED] p-7">
                        <h2 className="font-chango text-2xl leading-tight text-[#327560]">Math and brain-training games in one place</h2>
                        <p className="mt-4 leading-7 text-[#56635E]">MathMinds combines math practice with memory matching, concentration, logic, puzzles, and number games. Children can explore different activities while practicing important thinking skills.</p>
                    </article>
                    <article className="rounded-3xl bg-[#E7E0FA] p-7">
                        <h2 className="font-chango text-2xl leading-tight text-[#327560]">Learning at home or on the go</h2>
                        <p className="mt-4 leading-7 text-[#56635E]">MathMinds offers ad-free activities and offline access in its installed apps, so families can keep learning without a constant connection. Test selected activities before travelling because account and subscription features may still require internet access.</p>
                    </article>
                </div>
            </section>

            <section id="daily-challenges" className="bg-[#EAF5F0] scroll-mt-24">
                <div className="mx-auto max-w-[1000px] px-5 py-16 sm:px-8">
                    <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">Make math practice more fun with daily challenges</p>
                    <h2 className="mt-3 font-chango text-3xl leading-tight text-[#327560] sm:text-4xl">Daily Math Challenges and Leaderboard</h2>
                    <div className="mt-6 space-y-5 text-[17px] leading-8 text-[#56635E]">
                        <p>MathMinds turns math and brain-training activities into a fun daily challenge. Children earn points by playing games, see how they rank on the daily leaderboard, and discover yesterday’s top players.</p>
                        <p>Every day brings a fresh opportunity to participate, practice math skills, challenge themselves, and work toward a new achievement. The three highest-scoring players receive daily in-app gift rewards, adding an extra element of excitement to learning.</p>
                        <p>The MathMinds leaderboard is designed to make practicing counting, arithmetic, multiplication, memory, and other skills more engaging. Parents can use these daily challenges to encourage consistent practice while reminding children that learning, effort, and personal progress matter more than coming first.</p>
                        <p className="font-bold text-[#327560]">Play, practice, earn points, and celebrate every step forward with MathMinds!</p>
                    </div>
                    <NavLink to="/games/memory/leaderboard" className="mt-7 inline-block rounded-full bg-[#F9B818] px-6 py-3 font-bold shadow-sm hover:bg-[#FFCA47]">View the daily leaderboard</NavLink>

                    <div className="mt-12 border-t border-[#BFD9CF] pt-10">
                        <h2 className="font-chango text-2xl text-[#327560]">Daily leaderboard FAQ</h2>
                        <div className="mt-7 space-y-8">
                            <article>
                                <h3 className="text-xl font-bold text-[#283C43]">How does the MathMinds daily leaderboard work?</h3>
                                <p className="mt-3 leading-7 text-[#56635E]">Children earn points by playing MathMinds games. The daily leaderboard displays the highest-scoring players, and children can see who yesterday’s top players were.</p>
                            </article>
                            <article>
                                <h3 className="text-xl font-bold text-[#283C43]">Can children win rewards in MathMinds?</h3>
                                <p className="mt-3 leading-7 text-[#56635E]">Yes. The three highest-scoring players receive daily in-app gift rewards. The leaderboard gives children an additional goal to work toward as they enjoy math and brain-training games.</p>
                            </article>
                            <article>
                                <h3 className="text-xl font-bold text-[#283C43]">How can a leaderboard encourage math practice?</h3>
                                <p className="mt-3 leading-7 text-[#56635E]">A daily challenge can give children a clear goal, immediate feedback, and a reason to return to activities they enjoy. Parents can encourage children to focus on practicing skills, improving their own scores, and learning from mistakes—not only on winning.</p>
                            </article>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
